import { findGrowthExperiment, type ExperimentVariant } from '../data/growthExperiments';
import { trackEvent } from './analytics';

type ExperimentDecision = {
  experimentId: string;
  selectedVariantId: string;
  decidedAt: string;
  reason: string;
};

const DECISION_URL = '/data/experiment-decisions.json';
const STORAGE_PREFIX = 'propickleball_experiment_';

let decisionCache: Promise<ExperimentDecision[]> | null = null;

function readLocalVariant(experimentId: string, variantIds: string[]): string {
  try {
    const key = `${STORAGE_PREFIX}${experimentId}`;
    const stored = window.localStorage.getItem(key);
    if (stored && variantIds.includes(stored)) return stored;
    const seed = `${experimentId}:${window.navigator.userAgent}:${window.screen.width}x${window.screen.height}`;
    const hash = [...seed].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, 7);
    const selected = variantIds[hash % variantIds.length];
    window.localStorage.setItem(key, selected);
    return selected;
  } catch {
    return variantIds[0];
  }
}

async function loadExperimentDecisions(): Promise<ExperimentDecision[]> {
  if (!decisionCache) {
    decisionCache = fetch(DECISION_URL, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : []))
      .then((payload) => (Array.isArray(payload?.decisions) ? payload.decisions : []))
      .catch(() => []);
  }
  return decisionCache;
}

export async function selectExperimentVariant(experimentId: string): Promise<ExperimentVariant | null> {
  const experiment = findGrowthExperiment(experimentId);
  if (!experiment) return null;
  const variantIds = experiment.variants.map((variant) => variant.id);
  const decisions = await loadExperimentDecisions();
  const decidedVariantId = decisions.find((decision) => decision.experimentId === experimentId)?.selectedVariantId;
  const selectedVariantId = decidedVariantId && variantIds.includes(decidedVariantId)
    ? decidedVariantId
    : readLocalVariant(experimentId, variantIds);
  const variant = experiment.variants.find((item) => item.id === selectedVariantId) ?? experiment.variants[0];
  trackEvent({
    event: 'experiment_impression',
    location: experimentId,
    label: variant.id,
    routeFamily: 'experiment',
    placement: 'runtime_variant'
  });
  return variant;
}
